import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { registerOpenModal } from '../../hooks/useAnyModalOpen';
import './Modal.css';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const focusablesIn = (root) =>
  root ? Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => !el.closest('[hidden], [inert]')) : [];

// Open modals, newest last. Only the top one handles Escape and traps Tab.
const modalStack = [];

// Scroll lock shared by nested modals
let scrollLocks = 0;
let savedOverflow = '';
const lockScroll = () => {
  if (scrollLocks === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
};
const unlockScroll = () => {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = savedOverflow;
};

// Shared dialog: header (title + optional icon and close button), scrollable body and
// a footer that stays visible. Below 480px it becomes a bottom sheet.
//
//   <Modal open={show} onClose={close} title="Submit report?" tone="warning"
//          footer={<><button onClick={close}>Cancel</button><button>Submit</button></>}>
//     ...
//   </Modal>
//
// `dismissible={false}` disables Escape, overlay click and the close button (use while
// a submit is in flight). `initialFocusRef` picks the element focused on open; by
// default it's the first focusable element in the body, then the footer.
const Modal = ({
  open,
  onClose,
  title,
  titleIcon,
  size = 'md',
  dismissible = true,
  initialFocusRef,
  footer,
  tone = 'default',
  className = '',
  ariaLabel,
  children,
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const overlayPressRef = useRef(false);
  // Latest props for the document listeners, without re-running the open effect
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return undefined;

    const token = {};
    modalStack.push(token);
    const release = registerOpenModal();
    lockScroll();
    const returnFocusTo = document.activeElement;

    const target =
      initialFocusRef?.current ||
      focusablesIn(bodyRef.current)[0] ||
      focusablesIn(footerRef.current)[0] ||
      dialogRef.current;
    if (target) target.focus({ preventScroll: true });

    const handleKeyDown = (e) => {
      if (modalStack[modalStack.length - 1] !== token) return;

      if (e.key === 'Escape') {
        if (dismissibleRef.current) {
          e.stopPropagation();
          onCloseRef.current?.();
        }
        return;
      }

      if (e.key !== 'Tab') return;
      const items = focusablesIn(dialogRef.current);
      if (items.length === 0) {
        e.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = dialogRef.current?.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      const idx = modalStack.indexOf(token);
      if (idx !== -1) modalStack.splice(idx, 1);
      unlockScroll();
      release();
      if (returnFocusTo && typeof returnFocusTo.focus === 'function' && document.contains(returnFocusTo)) {
        returnFocusTo.focus({ preventScroll: true });
      }
    };
    // initialFocusRef is read once on open by design
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  // Close only when the press both started and ended on the overlay, so dragging a
  // text selection out of the dialog doesn't dismiss it.
  const handleOverlayMouseDown = (e) => {
    overlayPressRef.current = e.target === e.currentTarget;
  };
  const handleOverlayClick = (e) => {
    if (dismissible && overlayPressRef.current && e.target === e.currentTarget) onClose?.();
    overlayPressRef.current = false;
  };

  return createPortal(
    <div className="modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={`modal modal-${size} modal-tone-${tone} ${className}`.trim()}
        role={tone === 'danger' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
      >
        {(title || dismissible) && (
          <div className="modal-header">
            {titleIcon && <span className="modal-title-icon" aria-hidden="true">{titleIcon}</span>}
            {title && <h2 id={titleId} className="modal-title">{title}</h2>}
            {dismissible && (
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
                <X size={22} />
              </button>
            )}
          </div>
        )}
        <div ref={bodyRef} className="modal-body">
          {children}
        </div>
        {footer && (
          <div ref={footerRef} className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
